import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useOne, useTranslation } from "@refinedev/core";
import Button from "components/button";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps } from "components/modal";
import { RightSideModal } from "components/rightSideModal";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import { TextEditor } from "components/textEditor";
import {
  IMultilingualContent,
  ISimpleEventDetail,
  IPhaseTicketForm,
  Phase,
  PriceType,
  ProductType,
  SupportedLanguage,
  SupportedCurrency,
  UserType,
} from "interfaces";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { LANGUAGES, PRICE_TYPES, SELLING_TYPE } from "utills/constants";
import { z } from "zod";
import { ParentTicketSchema, SubTicketSchema } from "../schema";
import DateTimePicker from "components/datetimePicker";
import moment from "moment";
import { endpointService } from "components/shared/services/endpointService";
import { canAccessByType } from "utills/helpers";

interface TicketFormProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  ticketPhases: Phase[];
  onSubmit: (data: IPhaseTicketForm) => void;
  productType: ProductType;
}

interface IAttendantTemplateField {
  id: string;
  template_id: string;
  name: IMultilingualContent;
}

const PhaseTicketSchema = (languages: SupportedLanguage[], currency: SupportedCurrency) => {
  return z.object({
    ticket: ParentTicketSchema(languages),
    sub_ticket: SubTicketSchema(languages, currency),
  });
};

const DEFAULLT_TEMPLATE = [
  {
    template_id: "",
    name: {
      en: "",
      vi: "",
    },
  },
];

const DEFAULT_TICKET_VALUE = {
  ticket: {
    category: {
      en: "",
      vi: "",
    },
    selling_min_qty: 1,
    selling_max_qty: 10,
    selling_type: "multiple",
    is_package: false,
    attendant_template: DEFAULLT_TEMPLATE,
    is_use_private_flow: false,
    is_use_actiup_private_flow: false,
  },
  sub_ticket: {
    type_name: {
      en: "",
      vi: "",
    },
    short_description: {
      en: "",
      vi: "",
    },
    price_type: PriceType.PAID,
    original_price: 0,
    price: 0,
    sell_from: moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    sell_to: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    is_accept_edit_participant_info: true,
    is_accept_upgrade_ticket: false,
    is_accept_transfer_ticket: false,
    is_soldout: false,
    is_active: true,
    ticket_phase: "",
    hide_in_ui: false,
  },
};

enum PhaseTicketFormStep {
  PARENT_TICKET = "ticket",
  SUB_TICKET = "sub_ticket",
}

export const TicketForm = (props: TicketFormProps) => {
  const { translate } = useTranslation();
  const [ticketFormStep, setTicketFormStep] = useState<PhaseTicketFormStep>(
    PhaseTicketFormStep.PARENT_TICKET
  );
  const { open, onClose, eventDetailData, ticketPhases, productType } = props;
  const isAdmin = canAccessByType(UserType.ADMIN);
  const canUseActiupPrivateFlow = isAdmin && productType === ProductType.TICKET;

  const { data } = useOne({
    resource: endpointService.getEventEndpoint(
      "getAttendantTemplateByEventId",
      {
        eventId: eventDetailData.id ?? "",
      }
    ),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventDetailData.id,
    } as any,
  });

  const templatesDataArr = (data?.data ?? []).map((template: any) => ({
    label: get(template, "attendant_template_name", ""),
    value: get(template, "id", ""),
  }));

  const {
    control,
    handleSubmit,
    register,
    watch,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IPhaseTicketForm, HttpError, IPhaseTicketForm>({
    defaultValues: DEFAULT_TICKET_VALUE,
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        PhaseTicketSchema(eventDetailData.languages, eventDetailData.currency)
      );
      return createResolver(values, context, options);
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "ticket.attendant_template",
  });

  useEffect(() => {
    if (open) {
      setTicketFormStep(PhaseTicketFormStep.PARENT_TICKET);
      reset({ ...DEFAULT_TICKET_VALUE });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const _handleSubmitParentTicket = () => {
    trigger("ticket").then((isValid) => {
      if (isValid) {
        setTicketFormStep(PhaseTicketFormStep.SUB_TICKET);
      }
    });
  };

  const onSubmit = (data: IPhaseTicketForm) => {
    const ticket = canUseActiupPrivateFlow
      ? data.ticket
      : (() => {
          const { is_use_actiup_private_flow, ...submitTicket } = data.ticket;
          return submitTicket;
        })();
    const submitData: IPhaseTicketForm = {
      ticket: {
        ...ticket,
        product_type: productType,
      },
      sub_ticket: data.sub_ticket,
    };
    props.onSubmit(submitData);
  };

  const _submit = () => {
    if (ticketFormStep === PhaseTicketFormStep.PARENT_TICKET) {
      _handleSubmitParentTicket();
    } else {
      handleSubmit(onSubmit)();
    }
  };

  const ticketData = watch("ticket");

  const firstLanguage = eventDetailData.languages.includes(SupportedLanguage.EN)
    ? "en"
    : eventDetailData.languages[0] ?? "vi";

  const ticketPhaseArray = (ticketPhases ?? [])
    .filter((phase) => phase.is_active === true)
    .map((phase) => ({
      label: get(phase, `name.${firstLanguage}`, ""),
      value: get(phase, "id", ""),
    }));

  return (
    <RightSideModal
      title={translate("eventSetup.phaseAndTicket.modal.ticket.ticketTitle", {
        action: translate("add"),
      })}
      open={open}
      closeOnClickBackdrop={false}
      onClose={onClose}
      footer={
        <div className="w-full flex flex-col sm:flex-row gap-3 items-center">
          <Button
            className="w-full uppercase bg-white border-none text-brand-500 ring-1 ring-inset ring-brand-500 hover:bg-gray-50 active:bg-gray-100"
            onClick={() =>
              ticketFormStep === PhaseTicketFormStep.PARENT_TICKET
                ? onClose()
                : setTicketFormStep(PhaseTicketFormStep.PARENT_TICKET)
            }
          >
            {ticketFormStep === PhaseTicketFormStep.PARENT_TICKET
              ? translate("button.cancel")
              : translate("button.back")}
          </Button>
          <Button className="w-full uppercase" onClick={_submit}>
            {ticketFormStep === PhaseTicketFormStep.PARENT_TICKET
              ? translate(
                  "eventSetup.phaseAndTicket.modal.ticket.nextAndSetupSubTicket"
                )
              : translate("button.save")}
          </Button>
        </div>
      }
    >
      <div className="p-4 sm:px-6">
        {ticketFormStep === PhaseTicketFormStep.PARENT_TICKET ? (
          <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4">
            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">
                {translate("eventSetup.phaseAndTicket.modal.ticket.ticketInfo")}
              </p>
            </div>
            <div className="col-span-full">
              <Label
                required
                id={`category_${eventDetailData.languages[0] ?? "en"}`}
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.category"
                )}
              />
              <div className="flex flex-col gap-y-4">
                {eventDetailData.languages.map((lang) => (
                  <div key={lang}>
                    <div className="relative" key={lang}>
                      <Input
                        id={`category_${lang}`}
                        type="text"
                        className="pr-11"
                        placeholder={`${translate(
                          "eventSetup.phaseAndTicket.modal.ticket.category"
                        )} (${lang})`}
                        {...register(`ticket.category.${lang}`)}
                        error={get(
                          errors,
                          `ticket.category.${lang}.message`,
                          ""
                        )}
                      />
                      <img
                        src={`/images/flags/circle-${lang}.png`}
                        alt="Language Icon"
                        className="absolute top-1/2 right-3 -translate-y-1/2"
                      />
                    </div>
                    {get(errors, `ticket.category.${lang}`, "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors, `ticket.category.${lang}.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label
                id="selling_min_qty"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
                )}
                required
              />
              <Input
                id="selling_min_qty"
                type="number"
                min={0}
                placeholder={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
                )}
                {...register(`ticket.selling_min_qty`, {
                  valueAsNumber: true,
                })}
                error={get(errors, `ticket.selling_min_qty.message`, "")}
              />
              {get(errors, `ticket.selling_min_qty`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `ticket.selling_min_qty.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="selling_max_qty"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.maxSelectionQuantity"
                )}
                required
              />
              <Input
                id="selling_max_qty"
                type="number"
                min={0}
                placeholder={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.maxSelectionQuantity"
                )}
                {...register(`ticket.selling_max_qty`, {
                  valueAsNumber: true,
                })}
                error={get(errors, `ticket.selling_max_qty.message`, "")}
              />
              {get(errors, `ticket.selling_max_qty`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `ticket.selling_max_qty.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="selling_type"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.sellingType"
                )}
                required
              />
              <Controller
                control={control}
                name="ticket.selling_type"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <Select
                      key="selling_type"
                      inputRef={ref}
                      options={SELLING_TYPE}
                      id="selling_type"
                      placeholder={translate(
                        "eventSetup.phaseAndTicket.modal.ticket.sellingType"
                      )}
                      defaultValue={
                        SELLING_TYPE.find(
                          (selected: { label: string; value: string }) =>
                            selected.value === value
                        ) ?? ""
                      }
                      value={SELLING_TYPE.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      )}
                      onChange={(selected: {
                        label: string;
                        value: string;
                      }) => {
                        onChange(selected?.value);
                      }}
                      error={get(errors, `ticket.selling_type`, "")}
                    />
                  );
                }}
              />
              {get(errors, `ticket.selling_type`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `ticket.selling_type.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="ticket.is_use_private_flow"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.useForPrivateFlow"
                )}
              />
              <Controller
                control={control}
                name="ticket.is_use_private_flow"
                render={({ field: { onChange, value = true, ref } }) => (
                  <Input
                    type="checkbox"
                    label=""
                    id="ticket.is_use_private_flow"
                    checked={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChange(e.target.checked)
                    }
                  />
                )}
              />
              {get(errors, `ticket.is_use_private_flow`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `ticket.is_use_private_flow.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            {canUseActiupPrivateFlow ? (
              <div className="sm:col-span-3">
                <Label id="ticket.is_use_actiup_private_flow" name="Use for ActiUp private flow" />
                <Controller
                  control={control}
                  name="ticket.is_use_actiup_private_flow"
                  render={({ field: { onChange, value = true, ref } }) => (
                    <Input
                      type="checkbox"
                      label=""
                      id="ticket.is_use_actiup_private_flow"
                      checked={value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(e.target.checked)
                      }
                    />
                  )}
                />
                {get(errors, `ticket.is_use_actiup_private_flow`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `ticket.is_use_actiup_private_flow.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : <></>}
            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">
                {translate(
                  "eventSetup.phaseAndTicket.modal.ticket.participantFormInfo"
                )}
              </p>
            </div>
            <div className="col-span-full">
              <Controller
                control={control}
                name="ticket.is_package"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    label={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.isPackage"
                    )}
                    checked={value ?? false}
                    onChange={(isChecked: boolean) => {
                      onChange(isChecked);
                      replace(DEFAULLT_TEMPLATE);
                      if (isChecked) {
                        setValue("sub_ticket.is_accept_upgrade_ticket", false, {
                          shouldValidate: true,
                        });
                        setValue(
                          "sub_ticket.is_accept_transfer_ticket",
                          false,
                          { shouldValidate: true }
                        );
                      }
                    }}
                  />
                )}
              />
              {get(errors, `ticket.is_package`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `ticket.is_package.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="col-span-full">
              {watch("ticket.is_package") ? (
                <button
                  type="button"
                  className="flex flex-row items-center w-fit text-sm bg-white text-brand-500 active:bg-gray-50"
                  onClick={() => append(DEFAULLT_TEMPLATE)}
                >
                  <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                  <span className="shrink-0 text-sm">
                    {translate(
                      "eventSetup.phaseAndTicket.modal.ticket.addParticipantForms"
                    )}
                  </span>
                </button>
              ) : (
                <></>
              )}
              <div className="divide-y divide-gray-300">
                {fields.map((field: IAttendantTemplateField, index: number) => (
                  <div key={field.id} className="py-4">
                    {watch("ticket.is_package") && fields.length > 1 ? (
                      <div className="col-span-full flex justify-end">
                        <button onClick={() => remove(index)}>
                          <TrashIcon className="w-6 h-6 text-brand-500" />
                        </button>
                      </div>
                    ) : (
                      <></>
                    )}
                    <div>
                      <Label
                        id={`attendant_template.${index}.template_id`}
                        name={translate(
                          "eventSetup.phaseAndTicket.modal.ticket.selectParticipantForm"
                        )}
                        required
                      />
                      <Controller
                        control={control}
                        name={`ticket.attendant_template.${index}.template_id`}
                        render={({ field: { onChange, value = "", ref } }) => {
                          return (
                            <Select
                              key={`attendant_template.${index}.template_id`}
                              inputRef={ref}
                              options={templatesDataArr}
                              id="template_id"
                              placeholder={translate(
                                "eventSetup.phaseAndTicket.modal.ticket.selectTemplate"
                              )}
                              defaultValue={
                                templatesDataArr.find(
                                  (selected: {
                                    label: string;
                                    value: string;
                                  }) => selected.value === value
                                ) ?? ""
                              }
                              value={templatesDataArr.find(
                                (selected: { label: string; value: string }) =>
                                  selected.value === value
                              )}
                              onChange={(selected: {
                                label: string;
                                value: string;
                              }) => {
                                onChange(selected?.value);
                              }}
                              error={get(
                                errors,
                                `ticket.attendant_template.${index}.template_id`,
                                ""
                              )}
                            />
                          );
                        }}
                      />
                      {get(
                        errors,
                        `ticket.attendant_template.${index}.template_id`,
                        ""
                      ) ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(
                              errors,
                              `ticket.attendant_template.${index}.template_id.message`,
                              ""
                            )}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    {watch("ticket.is_package") ? (
                      <div className="mt-4">
                        <Label
                          id={`attendant_template.${index}.name`}
                          name={translate(
                            "eventSetup.phaseAndTicket.modal.ticket.formNameDisplayed"
                          )}
                        />
                        <div className="flex flex-col gap-y-4">
                          {eventDetailData.languages.map((lang) => (
                            <div key={lang}>
                              <div className="relative">
                                <Input
                                  id={`attendant_template.${index}.name.${lang}`}
                                  type="text"
                                  className="pr-11"
                                  placeholder={`${translate(
                                    "eventSetup.phaseAndTicket.modal.ticket.formNameDisplayed"
                                  )} (${lang})`}
                                  {...register(
                                    `ticket.attendant_template.${index}.name.${lang}`
                                  )}
                                  error={get(
                                    errors,
                                    `ticket.attendant_template.${index}.name.${lang}.message`,
                                    ""
                                  )}
                                />
                                <img
                                  src={`/images/flags/circle-${lang}.png`}
                                  alt="Language Icon"
                                  className="absolute top-1/2 right-3 -translate-y-1/2"
                                />
                              </div>
                              {get(
                                errors,
                                `ticket.attendant_template.${index}.name.${lang}`,
                                ""
                              ) ? (
                                <p className="error-text">
                                  <span className="font-medium">
                                    {get(
                                      errors,
                                      `ticket.attendant_template.${index}.name.${lang}.message`,
                                      ""
                                    )}
                                  </span>
                                </p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                ))}
                {get(errors, `ticket.attendant_template`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `ticket.attendant_template.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
        {ticketFormStep === PhaseTicketFormStep.SUB_TICKET ? (
          <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4">
            <div className="col-span-full">
              <Controller
                control={control}
                name="sub_ticket.price_type"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <div className="flex flex-row flex-wrap gap-x-3">
                      {PRICE_TYPES.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          className={`rounded-md ${
                            type.value === value
                              ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300"
                              : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          } px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                          onClick={() => {
                            onChange(type.value);
                            if (type.value === PriceType.FREE) {
                              setValue("sub_ticket.original_price", 0, {
                                shouldValidate: true,
                              });
                              setValue("sub_ticket.price", 0, {
                                shouldValidate: true,
                              });
                            }
                          }}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  );
                }}
              />
              {get(errors, `sub_ticket.price_type`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.price_type.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">
                {translate(
                  "eventSetup.phaseAndTicket.modal.ticket.subTicketInfo"
                )}
              </p>
            </div>
            <div className="col-span-full flex items-center justify-between font-medium">
              <p className="text-sm">
                {translate("eventSetup.phaseAndTicket.modal.ticket.category")}
              </p>
              <p>{get(ticketData, `category.${firstLanguage}`)}</p>
            </div>

            <div className="col-span-full">
              <Label
                id="ticket_phase"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.ticketPhase"
                )}
                required
              />
              <Controller
                control={control}
                name="sub_ticket.ticket_phase"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <Select
                      key="ticket_phase"
                      inputRef={ref}
                      options={ticketPhaseArray}
                      id="sub_ticket.ticket_phase"
                      placeholder={translate(
                        "eventSetup.phaseAndTicket.modal.ticket.selectTicketPhase"
                      )}
                      defaultValue={
                        ticketPhaseArray.find(
                          (selected: { label: string; value: string }) =>
                            selected.value === value
                        ) ??
                        (ticketPhases ?? []).find((phase) => phase.id === value)
                          ? {
                              label: get(
                                (ticketPhases ?? []).find(
                                  (phase) => phase.id === value
                                ),
                                `name.${firstLanguage}`,
                                ""
                              ),
                              value: value,
                            }
                          : ""
                      }
                      value={ticketPhaseArray.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      )}
                      onChange={(selected: {
                        label: string;
                        value: string;
                      }) => {
                        onChange(selected?.value);
                      }}
                      error={get(errors, `sub_ticket.ticket_phase`, "")}
                    />
                  );
                }}
              />
              {get(errors, `sub_ticket.ticket_phase`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.ticket_phase.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="col-span-full">
              <Label
                required
                id={`type_name_${eventDetailData.languages[0] ?? "en"}`}
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.ticketNameDisplayed"
                )}
              />
              <div className="flex flex-col gap-y-4">
                {eventDetailData.languages.map((lang) => (
                  <div key={lang}>
                    <div className="relative" key={lang}>
                      <Input
                        id={`type_name_${lang}`}
                        type="text"
                        className="pr-11"
                        placeholder={`${translate(
                          "eventSetup.phaseAndTicket.modal.ticket.ticketNameDisplayed"
                        )} (${lang})`}
                        {...register(`sub_ticket.type_name.${lang}`)}
                        error={get(
                          errors,
                          `sub_ticket.type_name.${lang}.message`,
                          ""
                        )}
                      />
                      <img
                        src={`/images/flags/circle-${lang}.png`}
                        alt="Language Icon"
                        className="absolute top-1/2 right-3 -translate-y-1/2"
                      />
                    </div>
                    {get(errors, `sub_ticket.type_name.${lang}`, "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `sub_ticket.type_name.${lang}.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-full">
              <Label
                id={`short_description_${eventDetailData.languages[0] ?? "en"}`}
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.noteBelowTicketName"
                )}
              />
              <div className="flex flex-col gap-y-4">
                {eventDetailData.languages.map((lang: SupportedLanguage) => (
                  <div key={lang}>
                    <div
                      className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                    >
                      <img
                        src={`/images/flags/circle-${lang}.png`}
                        alt="Language Icon"
                        className="w-4 shrink-0"
                      />
                      <p>
                        {
                          LANGUAGES.find((language) => language.value === lang)
                            ?.label
                        }
                      </p>
                    </div>

                    <Controller
                      control={control}
                      name={`sub_ticket.short_description.${lang}`}
                      render={({ field: { onChange, value = "" } }) => (
                        <TextEditor
                          key={`short_description.${lang}`}
                          value={value ?? ""}
                          onChange={onChange}
                          placeholder={`${translate(
                            "eventSetup.phaseAndTicket.modal.ticket.noteBelowTicketName"
                          )} (${lang})`}
                        />
                      )}
                    />

                    {get(errors, `sub_ticket.short_description.${lang}`, "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `sub_ticket.short_description.${lang}.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-full">
              <Label
                id="sub_ticket.total_for_sell"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.totalForSell"
                )}
              />
              <Input
                id="sub_ticket.total_for_sell"
                type="number"
                min={0}
                placeholder={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.totalForSell"
                )}
                {...register(`sub_ticket.total_for_sell`, {
                  valueAsNumber: true,
                })}
                error={get(errors, `sub_ticket.total_for_sell.message`, "")}
              />
              {get(errors, `sub_ticket.total_for_sell`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.total_for_sell.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">
                {translate("eventSetup.phaseAndTicket.modal.ticket.priceInfo")}
              </p>
            </div>
            <div className="sm:col-span-3">
              <Label
                id="original_price"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.originalPrice"
                )}
                required
              />
              <div className="flex">
                <Input
                  id="original_price"
                  type="number"
                  decimal={2}
                  min={0}
                  className="rounded-r-none"
                  placeholder={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.originalPrice"
                  )}
                  disabled={watch("sub_ticket.price_type") === PriceType.FREE}
                  error={get(errors, "sub_ticket.original_price")}
                  {...register("sub_ticket.original_price", {
                    valueAsNumber: true,
                  })}
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                  {eventDetailData.currency}
                </span>
              </div>
              {get(errors, `sub_ticket.original_price`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, "sub_ticket.original_price.message", "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="price"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.sellPrice"
                )}
                required
              />
              <div className="flex">
                <Input
                  id="price"
                  type="number"
                  decimal={2}
                  min={0}
                  className="rounded-r-none"
                  placeholder={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.sellPrice"
                  )}
                  disabled={watch("sub_ticket.price_type") === PriceType.FREE}
                  error={get(errors, "sub_ticket.price")}
                  {...register("sub_ticket.price", {
                    valueAsNumber: true,
                  })}
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                  {eventDetailData.currency}
                </span>
              </div>
              {get(errors, `sub_ticket.price`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, "sub_ticket.price.message", "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="sell_from"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.startDate"
                )}
                required
              />
              <Controller
                control={control}
                name="sub_ticket.sell_from"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <DateTimePicker
                      id="sub_ticket.sell_from"
                      placeholder={translate(
                        "eventSetup.phaseAndTicket.modal.ticket.startDate"
                      )}
                      error={get(errors, `sub_ticket.sell_from.message`, "")}
                      value={value}
                      onChange={(date: any) =>
                        date === null
                          ? onChange("")
                          : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))
                      }
                    />
                  );
                }}
              />
              {get(errors, `sub_ticket.sell_from`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.sell_from.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="sell_to"
                name={translate(
                  "eventSetup.phaseAndTicket.modal.ticket.endDate"
                )}
                required
              />
              <Controller
                control={control}
                name="sub_ticket.sell_to"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <DateTimePicker
                      id="sub_ticket.sell_to"
                      placeholder={translate(
                        "eventSetup.phaseAndTicket.modal.ticket.endDate"
                      )}
                      error={get(errors, `sub_ticket.sell_to.message`, "")}
                      value={value}
                      onChange={(date: any) =>
                        date === null
                          ? onChange("")
                          : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))
                      }
                    />
                  );
                }}
              />
              {get(errors, `sub_ticket.sell_to`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.sell_to.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">
                {translate(
                  "eventSetup.phaseAndTicket.modal.ticket.advanceSettings"
                )}
              </p>
            </div>
            <div className="col-span-full flex flex-col items-start gap-y-4">
              <Controller
                control={control}
                name="sub_ticket.hide_in_ui"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    label={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.show"
                    )}
                    hint={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.showHint"
                    )}
                    checked={!value}
                    onChange={(isChedked) => onChange(!isChedked)}
                  />
                )}
              />
              {get(errors, `sub_ticket.hide_in_ui`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.hide_in_ui.message`, "")}
                  </span>
                </p>
              ) : null}
              <Controller
                control={control}
                name="sub_ticket.is_accept_edit_participant_info"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    label={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.acceptEditParticipantInfo"
                    )}
                    hint={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.acceptEditParticipantInfoHint"
                    )}
                    checked={value ?? false}
                    onChange={onChange}
                  />
                )}
              />
              {get(errors, `sub_ticket.is_accept_edit_participant_info`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.is_accept_edit_participant_info.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
              <Controller
                control={control}
                name="sub_ticket.is_accept_upgrade_ticket"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    label={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.acceptUpgradeTicket"
                    )}
                    hint={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.acceptUpgradeTicketHint"
                    )}
                    disabled={watch("ticket.is_package")}
                    checked={value ?? false}
                    onChange={onChange}
                  />
                )}
              />
              {get(errors, `sub_ticket.is_accept_upgrade_ticket`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.is_accept_upgrade_ticket.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
              <Controller
                control={control}
                name="sub_ticket.is_accept_transfer_ticket"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    label={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.acceptTransferTicket"
                    )}
                    hint={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.acceptTransferTicketHint"
                    )}
                    disabled={watch("ticket.is_package")}
                    checked={value ?? false}
                    onChange={onChange}
                  />
                )}
              />
              {get(errors, `sub_ticket.is_accept_transfer_ticket`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.is_accept_transfer_ticket.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
              <Controller
                control={control}
                name="sub_ticket.is_soldout"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    label={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.isSoldOut"
                    )}
                    hint={translate(
                      "eventSetup.phaseAndTicket.modal.ticket.isSoldOutHint"
                    )}
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
              {get(errors, `sub_ticket.is_soldout`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.is_soldout.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </RightSideModal>
  );
};
